import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import {
  CertificateDesignerStore,
  CertificateElement,
  CertificateOrientation,
  CertificatePaperSize,
  CertificateTemplate,
  getJsPdfPageOptions,
  getPageSize,
  normalizeCertificatePaperSize,
} from "../certificatePrint/certificateTypes";
import { printCertificateSheet } from "../certificatePrint/certificateStorage";
import {
  cloneNewBillTemplate,
  DEFAULT_NEW_BILL_TEMPLATE_ID,
  DEFAULT_NEW_BILL_TEMPLATES,
  getDefaultNewBillTemplateById,
} from "./newBillTemplates";
import baseUrl from "../../../../api/baseUrl";

const STORAGE_KEY = "our_dental.newBillPrint.templates.v2";

export function createNewBillTemplateId() {
  return `tpl-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function builtinStore(): CertificateDesignerStore {
  return {
    templates: DEFAULT_NEW_BILL_TEMPLATES.map((template) =>
      cloneNewBillTemplate(template, { id: template.id, name: template.name })
    ),
    activeId: DEFAULT_NEW_BILL_TEMPLATE_ID,
    defaultId: DEFAULT_NEW_BILL_TEMPLATE_ID,
  };
}

const TABLE_FIELD_KEYS = new Set(["treatmentTable", "amountTable"]);

function ensureBuiltinElements(
  elements: CertificateElement[],
  builtin: CertificateTemplate
): CertificateElement[] {
  const have = new Set(elements.map((el) => el.id));
  const missing = builtin.elements
    .filter((el) => !have.has(el.id))
    .map((el) => ({ ...el }));
  return missing.length ? [...elements, ...missing] : elements;
}

function hydrateTemplate(raw: CertificateTemplate): CertificateTemplate {
  const builtin =
    getDefaultNewBillTemplateById(raw.id) || DEFAULT_NEW_BILL_TEMPLATES[0];
  const mapped =
    Array.isArray(raw.elements) && raw.elements.length > 0
      ? raw.elements.map((el) => {
          const next = TABLE_FIELD_KEYS.has(el.fieldKey || "")
            ? { ...el, type: "table" as const }
            : { ...el };
          if (next.fieldKey === "amountTable" && next.paddingMm == null) {
            next.paddingMm = 1.2;
          }
          return next;
        })
      : builtin.elements.map((el) => ({ ...el }));

  return {
    id: raw.id || `tpl-${Date.now()}`,
    name: raw.name || builtin.name,
    page: {
      ...builtin.page,
      ...raw.page,
      size: normalizeCertificatePaperSize(raw.page?.size, builtin.page.size),
    },
    elements: ensureBuiltinElements(mapped, builtin),
  };
}

export function isNewBillDesignerStore(raw: unknown): raw is CertificateDesignerStore {
  return Boolean(
    raw &&
      typeof raw === "object" &&
      Array.isArray((raw as CertificateDesignerStore).templates)
  );
}

export function hydrateNewBillStore(raw: unknown): CertificateDesignerStore {
  if (isNewBillDesignerStore(raw) && raw.templates.length) {
    const templates = raw.templates.map(hydrateTemplate);
    const activeId = templates.some((item) => item.id === raw.activeId)
      ? raw.activeId
      : templates[0].id;
    const defaultId = templates.some((item) => item.id === raw.defaultId)
      ? raw.defaultId
      : templates[0].id;
    return { templates, activeId, defaultId };
  }
  return builtinStore();
}

export function loadNewBillStore(): CertificateDesignerStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return builtinStore();
    return hydrateNewBillStore(JSON.parse(raw));
  } catch {
    return builtinStore();
  }
}

export function saveNewBillStore(store: CertificateDesignerStore) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* ignore quota / private mode */
  }
}

export function findNewBillTemplate(
  templates: CertificateTemplate[],
  id: string
): CertificateTemplate {
  return templates.find((item) => item.id === id) ?? templates[0];
}

export function getDefaultNewBillBuiltin(id: string) {
  return getDefaultNewBillTemplateById(id) || DEFAULT_NEW_BILL_TEMPLATES[0];
}

export const printNewBillSheet = printCertificateSheet;

/** Shared PDF builder — Download and Print must use this so layouts match. */
async function buildNewBillPdf(options: {
  stack: HTMLElement;
  size: CertificatePaperSize;
  orientation: CertificateOrientation;
}) {
  const papers = Array.from(
    options.stack.querySelectorAll<HTMLElement>(".certificate-paper")
  );
  const targets = papers.length ? papers : [options.stack];
  const page = getPageSize(options.orientation, options.size);
  const pdfOpts = getJsPdfPageOptions(options.orientation, options.size);
  const pdf = new jsPDF({
    orientation: pdfOpts.orientation,
    unit: pdfOpts.unit,
    format: pdfOpts.format,
  });

  for (let index = 0; index < targets.length; index += 1) {
    const target = targets[index];
    const guides = Array.from(
      target.querySelectorAll<HTMLElement>(
        ".certificate-safe-area, .certificate-resize-handle"
      )
    );
    guides.forEach((el) => {
      el.dataset.prevDisplay = el.style.display;
      el.style.display = "none";
    });

    const canvas = await html2canvas(target, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
      allowTaint: true,
    });

    guides.forEach((el) => {
      el.style.display = el.dataset.prevDisplay || "";
      delete el.dataset.prevDisplay;
    });

    if (index > 0) {
      pdf.addPage(pdfOpts.format, pdfOpts.orientation);
    }

    // Width-locked aspect fit (same as the working Download PDF path).
    const imgWidth = page.widthMm;
    const imgHeight = imgWidth / (canvas.width / canvas.height);
    pdf.addImage(
      canvas.toDataURL("image/png"),
      "PNG",
      0,
      0,
      imgWidth,
      Math.min(imgHeight, page.heightMm),
      "",
      "FAST"
    );
  }

  return pdf;
}

export async function downloadNewBillPdf(options: {
  stack: HTMLElement;
  size: CertificatePaperSize;
  orientation: CertificateOrientation;
  filename: string;
}) {
  const pdf = await buildNewBillPdf(options);
  pdf.save(options.filename);
}

/** Print uses the exact same PDF as Download so the dialog matches the template. */
export async function printNewBillViaPdf(options: {
  stack: HTMLElement;
  size: CertificatePaperSize;
  orientation: CertificateOrientation;
}) {
  const pdf = await buildNewBillPdf(options);
  const blobUrl = URL.createObjectURL(pdf.output("blob"));
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "none";
  document.body.appendChild(iframe);
  iframe.src = blobUrl;
  await new Promise<void>((resolve) => {
    iframe.onload = () => {
      setTimeout(() => {
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        resolve();
      }, 500);
    };
  });
}

/** Alias kept for older call sites — same as printNewBillViaPdf. */
export async function printNewBillNativeFit(options: {
  stack: HTMLElement;
  size: CertificatePaperSize;
  orientation: CertificateOrientation;
}) {
  await printNewBillViaPdf(options);
}

/** Invoice / Patient print — same PDF path as Manage Print Download. */
export async function fetchLatestNewBillStoreRaw(
  fallback?: unknown
): Promise<unknown> {
  try {
    const response = await baseUrl.get("/clinic/getBillData");
    const saved = response?.data?.data?.newBillPrint;
    if (saved) return saved;
  } catch {
    /* use fallback / local */
  }
  if (fallback) return fallback;
  try {
    const local = localStorage.getItem(STORAGE_KEY);
    if (local) return JSON.parse(local);
  } catch {
    /* ignore */
  }
  return fallback;
}

export async function printNewBillFromElement(
  host: HTMLElement | null,
  storeRaw?: unknown
) {
  if (!host) return;
  const raw = await fetchLatestNewBillStoreRaw(storeRaw);
  const store = hydrateNewBillStore(raw);
  const tpl = findNewBillTemplate(store.templates, store.defaultId || store.activeId);

  // Tell any mounted runtime to swap to the latest saved template (incl. halfLetter)
  // before html2canvas runs.
  window.dispatchEvent(
    new CustomEvent("newBillPrintRefresh", { detail: { store: raw } })
  );
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
  // Allow React to commit Half Letter (or other) page dimensions before capture.
  await new Promise<void>((resolve) => setTimeout(resolve, 150));

  await printNewBillViaPdf({
    stack: host,
    size: tpl.page.size,
    orientation: tpl.page.orientation,
  });
}
